/**
 * CompartirCartillaPage.js - Pagina fullscreen para compartir cartilla de vacunacion
 *
 * Accesible via: /cartilla?objetoId=...&uid=...&token=...
 * Muestra QR, link, lugares de confianza y boton Apple Wallet
 */
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import QRCode from 'qrcode';
import {
  compartirExpediente,
  regenerarTokenExpediente,
  desactivarCompartirExpediente,
  getLugaresConfianza,
  updateLugaresConfianza,
  obtenerInfoMascota
} from '../services/api';
import { getFavorites } from '../localizador/services/favoritesService';
import AppleWalletButton from '../components/AppleWalletButton';

export default function CompartirCartillaPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const objetoId = searchParams.get('objetoId');

  const [loading, setLoading] = useState(true);
  const [loadingShare, setLoadingShare] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [mascotaInfo, setMascotaInfo] = useState(null);
  const [shareToken, setShareToken] = useState('');

  // Estados para lugares de confianza
  const [incluirLugaresConfianza, setIncluirLugaresConfianza] = useState(false);
  const [lugaresSeleccionados, setLugaresSeleccionados] = useState([]);
  const [favoritosDisponibles, setFavoritosDisponibles] = useState([]);
  const [loadingFavoritos, setLoadingFavoritos] = useState(false);
  const [lugaresGuardados, setLugaresGuardados] = useState([]);
  const [editandoLugares, setEditandoLugares] = useState(false);

  // Cargar datos iniciales
  useEffect(() => {
    if (!objetoId) return;

    async function init() {
      setLoading(true);

      // Cargar info de mascota
      try {
        const info = await obtenerInfoMascota(objetoId);
        if (!info.error) {
          setMascotaInfo(info);
        }
      } catch (err) {
        console.error('Error cargando info mascota:', err);
      }

      // Generar/obtener link de compartir
      const resp = await compartirExpediente(objetoId);
      if (resp.error) {
        toast.error(resp.error);
        setLoading(false);
        return;
      }

      setShareUrl(resp.shareUrl);
      // Extraer token del shareUrl
      const urlParts = (resp.shareUrl || '').split('/');
      setShareToken(urlParts[urlParts.length - 1] || '');

      // Generar QR
      try {
        const qr = await QRCode.toDataURL(resp.shareUrl, { width: 256, margin: 2 });
        setQrDataUrl(qr);
      } catch (error) {
        console.error('Error generando QR:', error);
      }

      // Cargar lugares de confianza guardados
      await cargarLugaresGuardados();

      setLoading(false);
    }

    init();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objetoId]);

  // === FUNCIONES DE COMPARTIR ===

  async function handleRegenerarToken() {
    if (!window.confirm('Al regenerar el link, el anterior dejara de funcionar. ¿Continuar?')) {
      return;
    }

    setLoadingShare(true);
    const resp = await regenerarTokenExpediente(objetoId);

    if (resp.error) {
      toast.error(resp.error);
      setLoadingShare(false);
      return;
    }

    setShareUrl(resp.shareUrl);
    const urlParts = (resp.shareUrl || '').split('/');
    setShareToken(urlParts[urlParts.length - 1] || '');

    try {
      const qr = await QRCode.toDataURL(resp.shareUrl, { width: 256, margin: 2 });
      setQrDataUrl(qr);
    } catch (error) {
      console.error('Error generando QR:', error);
    }

    setLoadingShare(false);
    toast.success('Link regenerado. El anterior ya no funciona.');
  }

  async function handleDesactivarCompartir() {
    if (!window.confirm('¿Desactivar el link de compartir? La cartilla ya no sera accesible publicamente.')) {
      return;
    }

    setLoadingShare(true);
    const resp = await desactivarCompartirExpediente(objetoId);

    if (resp.error) {
      toast.error(resp.error);
      setLoadingShare(false);
      return;
    }

    setShareUrl('');
    setQrDataUrl('');
    setShareToken('');
    setLoadingShare(false);
    setEditandoLugares(false);
    toast.success('Link desactivado');
    navigate(-1);
  }

  function handleCopiarLink() {
    navigator.clipboard.writeText(shareUrl);
    toast.success('Link copiado al portapapeles');
  }

  // === FUNCIONES DE LUGARES DE CONFIANZA ===

  async function cargarFavoritos() {
    if (favoritosDisponibles.length > 0) return;
    setLoadingFavoritos(true);
    try {
      const favoritos = await getFavorites();
      const favoritosConLugar = favoritos.filter(f => f.placeData || f.lugar);
      setFavoritosDisponibles(favoritosConLugar);
    } catch (err) {
      console.error('Error cargando favoritos:', err);
      toast.error('Error al cargar favoritos');
    } finally {
      setLoadingFavoritos(false);
    }
  }

  async function cargarLugaresGuardados() {
    try {
      const resp = await getLugaresConfianza(objetoId);
      if (resp.error) return;
      const lugares = resp.lugaresConfianza || [];
      setLugaresGuardados(lugares);
      if (lugares.length > 0) {
        setIncluirLugaresConfianza(true);
        setLugaresSeleccionados(lugares.map((l, idx) => ({
          id: `saved-${idx}`,
          nombre: l.nombre,
          categoria: l.categoria,
          telefono: l.telefono,
          direccion: l.direccion,
          lat: l.lat,
          lng: l.lng
        })));
      }
    } catch (err) {
      console.error('Error cargando lugares guardados:', err);
    }
  }

  async function handleToggleLugaresConfianza(checked) {
    setIncluirLugaresConfianza(checked);
    if (checked) {
      await cargarFavoritos();
    } else {
      setLugaresSeleccionados([]);
      await guardarLugaresConfianza([]);
    }
  }

  function handleToggleLugar(favorito) {
    const lugarData = favorito.placeData || favorito.lugar || {};
    const lugarId = favorito.id;

    setLugaresSeleccionados(prev => {
      const yaSeleccionado = prev.some(l => l.id === lugarId);
      if (yaSeleccionado) {
        return prev.filter(l => l.id !== lugarId);
      } else {
        if (prev.length >= 4) {
          toast.error('Máximo 4 lugares permitidos');
          return prev;
        }
        return [...prev, {
          id: lugarId,
          nombre: lugarData.nombre || favorito.nombre || 'Sin nombre',
          categoria: lugarData.categoria || favorito.categoria || '',
          telefono: lugarData.telefono || lugarData.phone || '',
          direccion: lugarData.direccion || lugarData.address || '',
          lat: lugarData.lat || lugarData.latitude || null,
          lng: lugarData.lng || lugarData.longitude || null
        }];
      }
    });
  }

  async function guardarLugaresConfianza(lugares) {
    const lugaresAGuardar = lugares.map(l => ({
      nombre: l.nombre,
      categoria: l.categoria,
      telefono: l.telefono,
      direccion: l.direccion,
      lat: l.lat,
      lng: l.lng
    }));
    const resp = await updateLugaresConfianza(objetoId, lugaresAGuardar);
    if (resp.error) {
      toast.error(resp.error);
      return false;
    }
    setLugaresGuardados(resp.lugaresConfianza || []);
    return true;
  }

  async function handleGuardarLugares() {
    if (lugaresSeleccionados.length === 0) {
      toast.error('Selecciona al menos un lugar');
      return;
    }
    setLoadingShare(true);
    const success = await guardarLugaresConfianza(lugaresSeleccionados);
    setLoadingShare(false);
    if (success) {
      toast.success(`${lugaresSeleccionados.length} lugares guardados`);
    }
  }

  function getIconoCategoria(categoria) {
    const iconos = {
      'veterinaria': '🏥',
      'farmacia': '💊',
      'pension': '🏠',
      'estilistas': '✂️',
      'parques': '🌳',
      'tiendas': '🏪',
      'cerrajeria': '🔑',
      'mecanico': '🔧',
      'conveniencia': '🏪'
    };
    return iconos[(categoria || '').toLowerCase()] || '📍';
  }

  // Loading state
  if (loading) {
    return (
      <div
        className="d-flex align-items-center justify-content-center"
        style={{ minHeight: '100vh', backgroundColor: '#f8f9fa' }}
      >
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p className="text-muted">Cargando cartilla...</p>
        </div>
      </div>
    );
  }

  const nombreMascota = mascotaInfo?.nombre || 'Mascota';

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
      {/* Header */}
      <div
        className="modal-header-gradient"
        style={{
          padding: 'var(--space-4)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <h5
          className="mb-0"
          style={{
            fontSize: 'var(--font-size-lg)',
            fontWeight: 'var(--font-weight-bold)',
            color: 'var(--white)'
          }}
        >
          <i className="bi bi-share-fill me-2"></i>
          Cartilla de {nombreMascota}
        </h5>
        <button
          type="button"
          className="btn btn-sm"
          onClick={() => navigate(-1)}
          style={{
            color: 'var(--white)',
            border: '1px solid rgba(255,255,255,0.3)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)'
          }}
        >
          <i className="bi bi-arrow-left me-1"></i>
          Volver
        </button>
      </div>

      {/* Content */}
      <div className="container py-4" style={{ maxWidth: '600px' }}>
        {/* Aviso de privacidad */}
        <div
          className="alert alert-info mb-4"
          style={{
            fontSize: 'var(--font-size-sm)',
            borderRadius: 'var(--radius-md)'
          }}
        >
          <i className="bi bi-shield-check me-2"></i>
          <strong>Privacidad:</strong> El link compartido NO incluye tus datos personales
          (correo, telefono, direccion). Solo muestra las vacunas y desparasitaciones de tu mascota.
        </div>

        {/* QR Code */}
        {qrDataUrl && (
          <div className="text-center mb-4">
            <div
              className="card card-elevated d-inline-block"
              style={{
                borderRadius: 'var(--radius-lg)',
                padding: 'var(--space-4)'
              }}
            >
              <img
                src={qrDataUrl}
                alt="QR Code de la cartilla"
                style={{
                  width: '220px',
                  height: '220px',
                  borderRadius: 'var(--radius-md)'
                }}
              />
            </div>
            <p
              className="text-muted mt-3"
              style={{ fontSize: 'var(--font-size-sm)' }}
            >
              Escanea el codigo QR para ver la cartilla
            </p>
          </div>
        )}

        {/* Link para copiar */}
        <div className="card card-elevated mb-4" style={{ borderRadius: 'var(--radius-lg)' }}>
          <div className="card-body" style={{ padding: 'var(--space-4)' }}>
            <label
              className="form-label"
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-semibold)'
              }}
            >
              Link para compartir:
            </label>
            <div className="input-group">
              <input
                type="text"
                className="form-control"
                value={shareUrl}
                readOnly
                style={{ fontSize: 'var(--font-size-sm)' }}
              />
              <button
                className="btn btn-primary"
                onClick={handleCopiarLink}
                title="Copiar link"
              >
                <i className="bi bi-clipboard"></i>
              </button>
            </div>
          </div>
        </div>

        {/* Apple Wallet Button */}
        <div className="text-center mb-4">
          <AppleWalletButton
            objetoId={objetoId}
            token={shareToken}
            nombreMascota={nombreMascota}
          />
        </div>

        {/* Seccion de Lugares de Confianza */}
        <div className="card card-elevated mb-4" style={{ borderRadius: 'var(--radius-lg)' }}>
          <div className="card-body" style={{ padding: 'var(--space-4)' }}>
            <h6 style={{ fontWeight: 'var(--font-weight-semibold)', marginBottom: 'var(--space-3)' }}>
              <i className="bi bi-geo-alt me-2"></i>
              Lugares de confianza
            </h6>

            {/* Si ya tiene lugares guardados y no está editando, mostrar resumen */}
            {lugaresGuardados.length > 0 && !editandoLugares ? (
              <div
                style={{
                  border: '1px solid var(--feature-200)',
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--space-3)',
                  backgroundColor: 'var(--feature-50)'
                }}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <i className="bi bi-geo-alt-fill me-2 text-success"></i>
                    <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)' }}>
                      {lugaresGuardados.length} lugar{lugaresGuardados.length !== 1 ? 'es' : ''} de confianza
                    </span>
                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-500)', marginTop: '4px' }}>
                      {lugaresGuardados.map(l => l.nombre).join(', ')}
                    </div>
                  </div>
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={async () => {
                      setEditandoLugares(true);
                      setIncluirLugaresConfianza(true);
                      await cargarFavoritos();
                    }}
                    style={{ fontSize: 'var(--font-size-xs)' }}
                  >
                    <i className="bi bi-pencil me-1"></i>
                    Editar
                  </button>
                </div>
              </div>
            ) : (
              <div className="form-check mb-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="incluirLugares"
                  checked={incluirLugaresConfianza}
                  onChange={(e) => handleToggleLugaresConfianza(e.target.checked)}
                />
                <label
                  className="form-check-label"
                  htmlFor="incluirLugares"
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-semibold)'
                  }}
                >
                  <i className="bi bi-geo-alt me-1"></i>
                  Incluir lugares de confianza
                </label>
              </div>
            )}

            {/* Lista de favoritos para seleccionar */}
            {incluirLugaresConfianza && (lugaresGuardados.length === 0 || editandoLugares) && (
              <div
                style={{
                  border: '1px solid var(--gray-200)',
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--space-3)',
                  backgroundColor: 'var(--gray-50)',
                  maxHeight: '300px',
                  overflowY: 'auto',
                  marginTop: 'var(--space-2)'
                }}
              >
                {loadingFavoritos ? (
                  <div className="text-center py-3">
                    <div className="spinner-border spinner-border-sm text-primary" role="status">
                      <span className="visually-hidden">Cargando...</span>
                    </div>
                    <p className="mb-0 mt-2 text-muted" style={{ fontSize: 'var(--font-size-xs)' }}>
                      Cargando favoritos...
                    </p>
                  </div>
                ) : favoritosDisponibles.length === 0 ? (
                  <div className="text-center py-3">
                    <i className="bi bi-star text-muted" style={{ fontSize: '1.5rem' }}></i>
                    <p className="mb-0 mt-2 text-muted" style={{ fontSize: 'var(--font-size-xs)' }}>
                      Aun no tienes favoritos. Busca servicios en el Localizador y guarda los que mas te gusten.
                    </p>
                  </div>
                ) : (
                  <>
                    {favoritosDisponibles.map((favorito) => {
                      const lugarData = favorito.placeData || favorito.lugar || {};
                      const nombre = lugarData.nombre || favorito.nombre || 'Sin nombre';
                      const categoria = lugarData.categoria || favorito.categoria || '';
                      const telefono = lugarData.telefono || lugarData.phone || '';
                      const isSelected = lugaresSeleccionados.some(l => l.id === favorito.id);

                      return (
                        <div
                          key={favorito.id}
                          className="form-check mb-2"
                          style={{
                            padding: '8px',
                            borderRadius: 'var(--radius-sm)',
                            backgroundColor: isSelected ? 'var(--feature-50)' : 'transparent',
                            border: isSelected ? '1px solid var(--feature-200)' : '1px solid transparent'
                          }}
                        >
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`lugar-${favorito.id}`}
                            checked={isSelected}
                            onChange={() => handleToggleLugar(favorito)}
                            disabled={!isSelected && lugaresSeleccionados.length >= 4}
                          />
                          <label
                            className="form-check-label d-flex align-items-center"
                            htmlFor={`lugar-${favorito.id}`}
                            style={{
                              fontSize: 'var(--font-size-sm)',
                              cursor: 'pointer',
                              width: '100%'
                            }}
                          >
                            <span style={{ marginRight: '8px' }}>
                              {getIconoCategoria(categoria)}
                            </span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {nombre}
                              </div>
                              {telefono && (
                                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-500)' }}>
                                  {telefono}
                                </div>
                              )}
                            </div>
                          </label>
                        </div>
                      );
                    })}

                    {/* Contador y botones */}
                    <div className="d-flex justify-content-between align-items-center mt-3 pt-2 border-top">
                      <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-500)' }}>
                        {lugaresSeleccionados.length} de 4 seleccionados
                      </span>
                      <div className="d-flex gap-2">
                        {editandoLugares && (
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => {
                              setEditandoLugares(false);
                              setLugaresSeleccionados(lugaresGuardados.map((l, idx) => ({
                                id: `saved-${idx}`,
                                nombre: l.nombre,
                                categoria: l.categoria,
                                telefono: l.telefono,
                                direccion: l.direccion,
                                lat: l.lat,
                                lng: l.lng
                              })));
                            }}
                            style={{ fontSize: 'var(--font-size-xs)' }}
                          >
                            Cancelar
                          </button>
                        )}
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={async () => {
                            await handleGuardarLugares();
                            setEditandoLugares(false);
                          }}
                          disabled={loadingShare || lugaresSeleccionados.length === 0}
                          style={{ fontSize: 'var(--font-size-xs)' }}
                        >
                          {loadingShare ? (
                            <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                          ) : (
                            <i className="bi bi-check2 me-1"></i>
                          )}
                          Guardar
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Botones de accion */}
        <div className="d-flex flex-column gap-2 mb-4">
          <button
            className="btn btn-outline-warning w-100"
            onClick={handleRegenerarToken}
            disabled={loadingShare}
            style={{ fontSize: 'var(--font-size-sm)' }}
          >
            <i className="bi bi-arrow-repeat me-2"></i>
            Regenerar Link (invalida el anterior)
          </button>
          <button
            className="btn btn-outline-danger w-100"
            onClick={handleDesactivarCompartir}
            disabled={loadingShare}
            style={{ fontSize: 'var(--font-size-sm)' }}
          >
            <i className="bi bi-x-circle me-2"></i>
            Desactivar Link
          </button>
        </div>
      </div>
    </div>
  );
}
